import {atom} from 'jotai';
import {atomWithStorage} from 'jotai/utils';
import {userAtom} from '@/store/user';
import {FP_API_V2_URL} from '@/util/constants/api';
import type {Style, SearchState} from '@/types/style';

export const starredImageMapAtom = atomWithStorage<Record<number, number>>(
    'starred_image_map',
    {}
);

interface StyleImage {
    id: number;
    url: string;
    is_starred?: boolean;
}

interface StyleWithImages {
    id: number;
    style_images?: StyleImage[];
    images?: Record<string, {url?: string}>;
}

export const getStyleImageUrl = (
    style: StyleWithImages,
    starredImageMap: Record<number, number>
): string | null => {
    const starredImageId = starredImageMap[style.id];

    if (
        starredImageId &&
        style.style_images &&
        Array.isArray(style.style_images)
    ) {
        const starredImage = style.style_images.find(
            (img: StyleImage) => img.id === starredImageId
        );

        if (starredImage) return starredImage.url;
    }

    if (
        style.style_images &&
        Array.isArray(style.style_images) &&
        style.style_images.length > 0
    ) {
        const starredImage = style.style_images.find(
            (img: StyleImage) => img.is_starred
        );

        if (starredImage) return starredImage.url;

        return style.style_images[0]?.url ?? null;
    }
    if (style.images) {
        const firstImage = Object.values(style.images)[0];

        if (firstImage?.url) {
            return firstImage.url;
        }
    }

    return null;
};

export const allRawStylesAtom = atom<Style[]>([]);
export const activeStyleAtom = atom<Style | null>(null);
export const selectedStylesAtom = atom<Style[]>([]);

export const stylesSearchAtom = atom<SearchState>({
    query: '',
    page: 0,
    perPage: 40,
    items: [],
    total: 0,
    loading: false,
    error: null,
    initialized: false,
    filter: {
        roomType: null,
        tags: [],
        category: null,
    },
});

export const getStylesAtom = atom(null, async (get, set) => {
    // Dynamic import to avoid circular dependency with actions/styles
    const {getStyles} = await import('@/actions/styles');
    await getStyles(get, set);
});

export const getAllStylesAtom = atom(null, async (get, set) => {
    set(stylesSearchAtom, (prev: SearchState) => ({
        ...prev,
        loading: true,
    }));

    try {
        const user = get(userAtom);
        const {selectedBrandingIdAtom} = await import('@/store/retailer-view');
        const selectedBrandingId = get(selectedBrandingIdAtom);

        // Use selected retailer's branding ID if in retailer view, otherwise use user's branding ID
        const baseUrl = `${FP_API_V2_URL}/styles.json`;
        let apiUrl = baseUrl;

        if (selectedBrandingId) {
            // In retailer view: filter by branding_id from retailer mapping
            apiUrl = `${baseUrl}?branding_id=${selectedBrandingId}`;
        } else if (user?.branding?.id) {
            // In normal view: filter by user's branding_id
            apiUrl = `${baseUrl}?branding_id=${user.branding.id}`;
        }

        const response = await fetch(apiUrl, {cache: 'no-cache'});
        let items: Style[] = await response.json();

        // No need to filter generic styles - branding_id filtering already returns only retailer-specific styles

        // Add starred image URLs
        items = items.map((style: Style) => ({
            ...style,
            starred_image_url: getStyleImageUrl(
                style,
                get(starredImageMapAtom)
            ),
        }));

        set(allRawStylesAtom, items ?? []);
        set(stylesSearchAtom, (prev: SearchState) => ({
            ...prev,
            total: items.length,
            page: 0,
            items: items.slice(0, prev.perPage),
            loading: false,
            initialized: true,
        }));
    } catch (error) {
        set(stylesSearchAtom, (prev: SearchState) => ({
            ...prev,
            error:
                error instanceof Error
                    ? error.message
                    : 'Failed to fetch styles',
            loading: false,
        }));
    }
});

const allTagsAtom = atom(async (get) => {
    try {
        const user = get(userAtom);

        if (!user) return [];

        const {listStyleTags} = await import('@/actions/styles');
        const {selectedBrandingIdAtom} = await import('@/store/retailer-view');
        const selectedBrandingId = get(selectedBrandingIdAtom);

        // Use retailer's branding ID if in retailer view, otherwise use user's branding ID
        const brandingId = selectedBrandingId
            ? String(selectedBrandingId)
            : user.branding?.id
              ? String(user.branding.id)
              : undefined;

        const tags = await listStyleTags(brandingId);

        return tags.sort();
    } catch {
        const allItems = get(allRawStylesAtom);
        const tags = new Set<string>();

        allItems.forEach((style) => {
            style.tags?.forEach((tag) => tags.add(tag));
        });

        return Array.from(tags).sort();
    }
});

export const topTagsAtom = atom(async (get) => {
    try {
        const allApiTags = await get(allTagsAtom);
        const allItems = get(allRawStylesAtom);
        const searchState = get(stylesSearchAtom);
        const selectedTags = searchState.filter.tags ?? [];

        // Filter styles to only those that have ALL selected tags
        const filteredItems =
            selectedTags.length > 0
                ? allItems.filter((style) =>
                      selectedTags.every((selectedTag) =>
                          style.tags?.includes(selectedTag)
                      )
                  )
                : allItems;

        const tagCounts = new Map<string, number>();

        // Count tags only from filtered items
        filteredItems.forEach((style) => {
            style.tags?.forEach((tag) => {
                tagCounts.set(tag, (tagCounts.get(tag) ?? 0) + 1);
            });
        });

        const tagsWithCounts = allApiTags.map((tag) => ({
            tag,
            count: tagCounts.get(tag) ?? 0,
        }));

        // Filter out tags with 0 count and sort by count
        return tagsWithCounts
            .filter((t) => t.count > 0)
            .sort((a, b) => b.count - a.count)
            .slice(0, 20);
    } catch {
        return [];
    }
});
